import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FiActivity, FiEdit3, FiPlus, FiSearch, FiUsers } from 'react-icons/fi';

import { adminLearningPathsApi } from '@/services/adminLearningPaths';
import { PATHS } from '@/routes/paths';
import { Button, ButtonIcon } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHeaderWrapper, TableNoData, TableRow } from '@/components/ui/table';
import { Filter, FilterContent, FilterInput } from '@/components/custom/filter';
import { Loader } from '@/components/custom/loader';
import { EmptyState } from '@/components/custom/empty-state';
import { TiaBanner } from '@/components/custom/tia-banner';
import { PlayfulCardButton } from '@/components/custom/playful-card';
import { DashboardContent } from '@/layouts/dashboard';
import { useSession } from '@/hooks/useSession';
import usePlayfulTeacher from '@/hooks/usePlayfulTeacher';
import { isTeacherOnly } from '@/lib/teacher-profile';
import { PENSANDO } from '@/pages/dashboard/home-teacher/teacher-character';

import EditTrilhaModal from './EditTrilhaModal';
import NovaTrilhaModal from './NovaTrilhaModal';
import TrilhaGenerationsModal from './TrilhaGenerationsModal';

const KIND_CONFIG = {
  adaptive: { label: 'Adaptativa', variant: 'info' },
  exam: { label: 'Matriz', variant: 'default' },
  curated: { label: 'Por objetivo', variant: 'neutral-lightest' }
};

const TABLE_HEAD = [
  { label: 'Trilha', width: 280 },
  { label: 'Slug', width: 220 },
  { label: 'Tipo', width: 110 },
  { label: 'Blocos', width: 90 },
  { label: 'Alunos (iniciaram · ativos · concluíram)', width: 260 },
  { label: 'Status', width: 100 },
  { label: 'Ações', width: 250 }
];

// Tema playful (professor): badges "soft", no padrão da lista de Desafio ao Vivo.
const PLAYFUL_KIND_BADGE = {
  adaptive: { label: 'Adaptativa', variant: 'info-soft' },
  exam: { label: 'Matriz', variant: 'amber-soft' },
  curated: { label: 'Por objetivo', variant: 'muted' }
};

const PLAYFUL_TABLE_HEAD = [
  { label: 'Trilha' },
  { label: 'Slug', width: 200 },
  { label: 'Tipo', width: 130 },
  { label: 'Blocos', width: 90 },
  { label: 'Alunos (iniciaram · ativos · concluíram)', width: 260 },
  { label: 'Status', width: 110 },
  { label: '', width: 150 }
];

function truncate(text = '', n = 90) {
  return text.length > n ? `${text.slice(0, n - 1)}…` : text;
}

function PlayfulTrilhaRow({ trilha }) {
  const navigate = useNavigate();
  const kind = PLAYFUL_KIND_BADGE[trilha.kind] || { label: trilha.kind, variant: 'muted' };
  return (
    <TableRow>
      <TableCell>
        <p
          className="mb-0 truncate text-sm font-bold"
          style={{ color: '#222731' }}
          title={trilha.name}
        >
          {trilha.name}
        </p>
        {trilha.description && <p className="mb-0 mt-0.5 truncate text-sm text-neutral-400">{truncate(trilha.description)}</p>}
      </TableCell>
      <TableCell
        className="truncate font-mono text-sm text-neutral-400"
        title={trilha.slug}
      >
        {trilha.slug}
      </TableCell>
      <TableCell>
        <Badge
          variant={kind.variant}
          size="sm"
          className="rounded-full text-sm"
        >
          {kind.label}
        </Badge>
      </TableCell>
      <TableCell className="text-sm tabular-nums">{trilha.blocks_available ?? '—'}</TableCell>
      <TableCell className="text-sm tabular-nums">
        {trilha.students_started ?? 0} · {trilha.students_active ?? 0} · {trilha.students_completed ?? 0}
      </TableCell>
      <TableCell>
        <Badge
          variant={trilha.active ? 'success-soft' : 'muted'}
          size="sm"
          className="rounded-full text-sm"
        >
          {trilha.active ? 'Ativa' : 'Inativa'}
        </Badge>
      </TableCell>
      <TableCell>
        <div className="flex justify-end">
          <PlayfulCardButton
            className="h-7 w-auto px-4 text-sm"
            onClick={() => navigate(PATHS.TRILHAS_ADMIN.STUDENTS(trilha.id))}
          >
            Ver alunos
          </PlayfulCardButton>
        </div>
      </TableCell>
    </TableRow>
  );
}

export default function TrilhasAdmin() {
  const navigate = useNavigate();
  const { session } = useSession();
  // Professor só acompanha os alunos: sem criar, editar nem acompanhar gerações.
  const teacherOnly = isTeacherOnly(session);
  const playfulTeacher = usePlayfulTeacher();
  const [paths, setPaths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editTarget, setEditTarget] = useState(null);
  const [progressOpen, setProgressOpen] = useState(false);
  const [lastGenerationId, setLastGenerationId] = useState(null);
  const [novaTrilhaOpen, setNovaTrilhaOpen] = useState(false);

  const breadcrumbs = {
    title: 'Trilhas',
    nav: [{ name: teacherOnly ? 'Trilhas' : 'Gerenciar trilhas', isActive: true }]
  };

  const load = () => {
    setLoading(true);
    return adminLearningPathsApi
      .index()
      .then(json => {
        const payload = json?.data || [];
        setPaths(payload.map(d => ({ id: d.id, ...d.attributes })));
      })
      .catch(() => {
        toast.error('Erro ao carregar trilhas');
        setPaths([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  // O catálogo de trilhas é pequeno (unidades) — busca client-side, sem paginação.
  const visible = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return paths;
    return paths.filter(p => p.name?.toLowerCase().includes(q) || p.slug?.toLowerCase().includes(q));
  }, [paths, searchTerm]);

  // Mesmo filtro de busca nos dois temas.
  const searchFilter = (
    <Filter
      variant="plain"
      className="mb-0 w-fit"
    >
      <FilterContent>
        <FilterInput
          label="Buscar"
          endIcon={FiSearch}
          wrapperClassName="min-w-[280px]"
          id="trilha-search"
          type="search"
          placeholder="Nome ou slug da trilha"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
      </FilterContent>
    </Filter>
  );

  // Playful (professor): mesma estrutura da lista de Desafio ao Vivo — banner, busca e tabela.
  if (playfulTeacher) {
    return (
      <DashboardContent
        variant="plain"
        breadcrumbs={breadcrumbs}
      >
        <div className="relative mb-6 sm:mt-16">
          <TiaBanner
            className="h-full"
            image={PENSANDO}
            imageAlt="Personagem do professor"
            imageHeight={190}
            imageOffset={150}
            bubbleCorner="tl"
            bubbleMaxWidth={520}
            text="Veja quem já começou cada trilha, até onde chegou e como está o desempenho 🧭"
          />
        </div>

        <div className="mb-4">{searchFilter}</div>

        {loading ? (
          <Loader />
        ) : visible.length > 0 ? (
          <Table className="min-w-[1100px]">
            <TableHeaderWrapper tableHead={PLAYFUL_TABLE_HEAD} />
            <TableBody>
              {visible.map(p => (
                <PlayfulTrilhaRow
                  key={p.id}
                  trilha={p}
                />
              ))}
            </TableBody>
          </Table>
        ) : paths.length === 0 ? (
          <EmptyState type="data" />
        ) : (
          <EmptyState
            type="data"
            text="Nenhuma trilha encontrada para este filtro."
          />
        )}
      </DashboardContent>
    );
  }

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
        {searchFilter}
        {!teacherOnly && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setLastGenerationId(null);
                setProgressOpen(true);
              }}
            >
              <ButtonIcon>
                <FiActivity />
              </ButtonIcon>{' '}
              Acompanhar gerações
            </Button>
            <Button onClick={() => setNovaTrilhaOpen(true)}>
              <ButtonIcon>
                <FiPlus />
              </ButtonIcon>{' '}
              Nova trilha
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        <Loader />
      ) : (
        <Table className="min-w-[1310px]">
          <TableHeaderWrapper tableHead={TABLE_HEAD} />
          <TableBody>
            {visible.map(p => {
              const kind = KIND_CONFIG[p.kind] || { label: p.kind, variant: 'neutral-lightest' };
              return (
                <TableRow key={p.id}>
                  <TableCell>
                    <p className="mb-0 text-sm font-medium text-primary-dark">{p.name}</p>
                    {p.description && <p className="mb-0 text-sm text-neutral">{truncate(p.description)}</p>}
                  </TableCell>
                  <TableCell
                    className="truncate font-mono text-sm text-neutral"
                    title={p.slug}
                  >
                    {p.slug}
                  </TableCell>
                  <TableCell>
                    <Badge
                      size="sm"
                      variant={kind.variant}
                    >
                      {kind.label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm tabular-nums">{p.blocks_available ?? '—'}</TableCell>
                  <TableCell className="text-sm tabular-nums">
                    {p.students_started ?? 0} · {p.students_active ?? 0} · {p.students_completed ?? 0}
                  </TableCell>
                  <TableCell>
                    <Badge
                      size="sm"
                      variant={p.active ? 'success' : 'neutral-lightest'}
                    >
                      {p.active ? 'Ativa' : 'Inativa'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {!teacherOnly && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mr-2"
                        onClick={() => setEditTarget(p)}
                      >
                        <ButtonIcon>
                          <FiEdit3 />
                        </ButtonIcon>{' '}
                        Editar
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(PATHS.TRILHAS_ADMIN.STUDENTS(p.id))}
                    >
                      <ButtonIcon>
                        <FiUsers />
                      </ButtonIcon>{' '}
                      Ver alunos
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
            <TableNoData
              notFound={visible.length === 0}
              colSpan={TABLE_HEAD.length}
            />
          </TableBody>
        </Table>
      )}

      <EditTrilhaModal
        trilha={editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={load}
      />

      <TrilhaGenerationsModal
        open={progressOpen}
        onOpenChange={setProgressOpen}
        highlightId={lastGenerationId}
      />

      <NovaTrilhaModal
        open={novaTrilhaOpen}
        onOpenChange={setNovaTrilhaOpen}
        onSuccess={created => {
          load();
          setLastGenerationId(created?.id || null);
          setProgressOpen(true);
        }}
      />
    </DashboardContent>
  );
}
